import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';

import '../styles/components/Paginator.less';

interface Props {
    currentPage: number;
    pageSize: number;
    totalItemsCount: number;
    setCurrentPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
}

interface Pager {
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    startPage: number;
    endPage: number;
    startIndex: number;
    endIndex: number;
    pages: Array<number>;
}

const Paginator = ({ currentPage, pageSize, totalItemsCount, setCurrentPage, setPageSize }: Props): ReactElement => {
    const [pager, setPager] = useState<Pager | null>(null);

    // Constructor
    useEffect(() => {
        window.onpopstate = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const page = parseInt(urlParams.get('page') as string, 10);
            setPage(page ?? 1);
            setCurrentPage(page ?? 1);
        };
    }, []);

    useEffect(() => {
        if (totalItemsCount && pageSize) {
            setPager(initPaginator(totalItemsCount, currentPage ? currentPage : 1, pageSize));
        }
    }, [totalItemsCount, pageSize]);

    useEffect(() => {
        if (!currentPage) {
            // set to initial page if no current page is defined
            setPage(1);
        } else {
            // set new page if either the current page or the total number of items changed
            setPage(currentPage);
        }
    }, [currentPage]);

    const pageSizeOptions = [10, 25, 50, 100];
    const currentPageSize = pageSize || 10;

    const setPage = (pageIndex: number) => {
        // get and set new pager object for specified page
        let paginator = null;
        if (totalItemsCount && pageSize) {
            paginator = initPaginator(totalItemsCount, currentPage ? currentPage : 1, pageSize);
            setPager(paginator);
        }

        // if page selected is out of bounds, we return
        if (paginator && (pageIndex < 1 || pageIndex > paginator.totalPages)) {
            return;
        }
        setCurrentPage(pageIndex);
    };

    const initPaginator = (
        totalCount: number | null,
        currentPage: number | null,
        pageSize: number | null
    ): Pager | null => {
        if (totalCount) {
            // default to first page
            const currPage = currentPage || 1;

            // default page size is 10
            const sizePage = pageSize || 10;

            // calculate total pages
            const totalPages = Math.ceil(totalCount / sizePage);

            let startPage = 1;
            let endPage = totalPages;

            if (totalPages > 10) {
                // more than 10 total pages so calculate start and end pages
                if (currPage <= 6) {
                    startPage = 1;
                    endPage = 10;
                } else if (currPage + 4 >= totalPages) {
                    startPage = totalPages - 9;
                    endPage = totalPages;
                } else {
                    startPage = currPage - 5;
                    endPage = currPage + 4;
                }
            }

            // calculate start and end item indexes
            const startIndex = (currPage - 1) * sizePage;
            const endIndex = Math.min(startIndex + sizePage, totalCount) - 1;

            // create an array of pages to ng-repeat in the pager control
            const pages = [...Array(endPage + 1 - startPage).keys()].map((i) => startPage + i);

            // return object with all pager properties required by the view
            return {
                totalCount: totalCount,
                currentPage: currPage,
                pageSize: sizePage,
                totalPages,
                startPage,
                endPage,
                startIndex,
                endIndex,
                pages
            };
        } else {
            return null;
        }
    };

    return (
        <div data-testid="pagination-component" className="paginator-container">
            <div className="table-count">
                <strong>{totalItemsCount} items</strong>
            </div>
            {pager && (
                <>
                    {pager.pages && pager.pages.length > 1 && (
                        <nav data-testid="page-navigation" aria-label="Page navigation">
                            <ul data-testid="pagination-list" className="pagination">
                                <li className={pager.currentPage === 1 ? 'disabled' : ''}>
                                    <span title="page-btn-first" onClick={() => setPage(1)}>
                                        &laquo;
                                    </span>
                                </li>
                                <li className={pager.currentPage === 1 ? 'disabled' : ''}>
                                    <span title="page-btn-prev" onClick={() => setPage(pager.currentPage - 1)}>
                                        &lsaquo;
                                    </span>
                                </li>
                                {pager.pages.map((page) => (
                                    <li key={page} className={pager.currentPage === page ? 'active' : ''}>
                                        <span title={`page-btn-${page}`} onClick={() => setPage(page)}>
                                            {page}
                                        </span>
                                    </li>
                                ))}
                                <li className={pager.currentPage === pager.totalPages ? 'disabled' : ''}>
                                    <span title="page-btn-next" onClick={() => setPage(pager.currentPage + 1)}>
                                        &rsaquo;
                                    </span>
                                </li>
                                <li className={pager.currentPage === pager.totalPages ? 'disabled' : ''}>
                                    <span title="page-btn-last" onClick={() => setPage(pager.totalPages)}>
                                        &raquo;
                                    </span>
                                </li>
                            </ul>
                        </nav>
                    )}
                    {pager.totalCount && (
                        <select
                            data-testid="dropdown-items-per-page"
                            id="dropdown-cal-per-page"
                            title={pager.pageSize ? pager.pageSize.toString() : '10'}
                            defaultValue={currentPageSize}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                setPageSize(parseInt(e.target.value, 10));
                            }}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    )}
                </>
            )}
        </div>
    );
};

export default Paginator;
